import {
  SelfSchedulerRow,
  SelfSchedulerRowResponse,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { type Sow } from "../capTypes";
import { getScheduledInstallServiceAppointment } from "../constants/capInstallScheduling";

/** Poll SOW refresh immediately, then every 15s for up to 60s after a 503 book response. */
const INSTALL_BOOKING_503_POLL_INTERVAL_MS = 15_000;
const INSTALL_BOOKING_503_POLL_ATTEMPTS = 5;

const resolveSelfSchedulerRow = (
  data: SelfSchedulerRowResponse | undefined
): SelfSchedulerRow | undefined => {
  if (!data) return undefined;
  return "self_scheduler_row" in data ? data.self_scheduler_row : data;
};

const getErrorMessage = (err: unknown): string => {
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object") {
    const record = err as Record<string, unknown>;
    if (typeof record.error_message === "string") return record.error_message;
    if (typeof record.message === "string") return record.message;
    const upstream = record.upstreamError;
    if (
      upstream &&
      typeof upstream === "object" &&
      typeof (upstream as { message?: string }).message === "string"
    ) {
      return (upstream as { message: string }).message;
    }
  }
  return "";
};

const isInstallBooking503Error = (err: unknown): boolean => {
  if (err && typeof err === "object") {
    const status = (err as { status?: number | string }).status;
    if (status === 503 || status === "503") return true;
  }
  const message = getErrorMessage(err);
  return /\b503\b/.test(message) || /service unavailable/i.test(message);
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const pollInstallBookingAfter503 = async (
  refreshSow: () => Promise<unknown>
): Promise<Sow> => {
  for (
    let attempt = 0;
    attempt < INSTALL_BOOKING_503_POLL_ATTEMPTS;
    attempt++
  ) {
    if (attempt > 0) {
      await sleep(INSTALL_BOOKING_503_POLL_INTERVAL_MS);
    }
    const sow = (await refreshSow()) as Sow | undefined;
    if (getScheduledInstallServiceAppointment(sow)) {
      return sow as Sow;
    }
  }

  throw new Error(
    "Install booking could not be confirmed after receiving a 503 response"
  );
};

export {
  isInstallBooking503Error,
  pollInstallBookingAfter503,
  resolveSelfSchedulerRow,
};
